import { create } from 'zustand';
import type { AppConfig, LayerConfig, Manifest } from '../api/types';
import { layerIndex, orderedThemes, themeDefaults } from '../lib/config';
import {
  applyMemory,
  browserStorage,
  loadMemory,
  recordChange,
  saveMemory,
  type VisMemory,
} from '../lib/visibilityMemory';

export interface PopupState {
  layerId: string;
  properties: Readonly<Record<string, unknown>>;
  point: { x: number; y: number };
}

interface MapState {
  config: AppConfig | null;
  layers: ReadonlyMap<string, LayerConfig>;
  themeId: string | null;
  /** effective per-node visibility for the active theme (defaults + remembered changes) */
  visibility: Readonly<Record<string, boolean>>;
  memory: VisMemory;
  zoom: number;
  manifest: Manifest | null;
  basemapId: string;
  dataUpdatedAt: Date | null;
  /** A quiet one-line notice for the status line (e.g. a basemap fallback). */
  notice: string | null;
  popup: PopupState | null;
  init: (config: AppConfig, basemapId: string) => void;
  setTheme: (themeId: string) => void;
  setNodeVisible: (key: string, visible: boolean) => void;
  setZoom: (zoom: number) => void;
  setManifest: (manifest: Manifest) => void;
  markDataUpdated: (at: Date) => void;
  setBasemap: (id: string) => void;
  setNotice: (notice: string | null) => void;
  setPopup: (popup: PopupState | null) => void;
}

function visibilityFor(
  config: AppConfig,
  layers: ReadonlyMap<string, LayerConfig>,
  themeId: string,
  memory: VisMemory,
) {
  const theme = config.themes.find((t) => t.id === themeId);
  const defaults = theme ? themeDefaults(theme, layers) : {};
  return { defaults, ...applyMemory(memory, themeId, defaults) };
}

export const useMapStore = create<MapState>()((set, get) => ({
  config: null,
  layers: new Map(),
  themeId: null,
  visibility: {},
  memory: {},
  zoom: 0,
  manifest: null,
  basemapId: 'none',
  dataUpdatedAt: null,
  notice: null,
  popup: null,

  init(config, basemapId) {
    const layers = layerIndex(config.layers);
    const first = orderedThemes(config)[0];
    const stored = loadMemory(browserStorage());
    const themeId = first?.id ?? null;
    const applied = themeId ? visibilityFor(config, layers, themeId, stored) : { visibility: {}, memory: stored };
    saveMemory(browserStorage(), applied.memory);
    set({
      config,
      layers,
      themeId,
      basemapId,
      visibility: applied.visibility,
      memory: applied.memory,
      zoom: first?.camera.zoom ?? 0,
    });
  },

  setTheme(themeId) {
    const { config, layers, memory, themeId: current } = get();
    if (!config || themeId === current) return;
    const applied = visibilityFor(config, layers, themeId, memory);
    saveMemory(browserStorage(), applied.memory);
    set({ themeId, visibility: applied.visibility, memory: applied.memory, popup: null });
  },

  setNodeVisible(key, visible) {
    const { config, layers, themeId, memory, visibility } = get();
    if (!config || !themeId) return;
    const { defaults } = visibilityFor(config, layers, themeId, {});
    const def = defaults[key];
    if (def === undefined) return;
    const nextMemory = recordChange(memory, themeId, key, visible, def);
    saveMemory(browserStorage(), nextMemory);
    set({ memory: nextMemory, visibility: { ...visibility, [key]: visible } });
  },

  setZoom: (zoom) => set({ zoom }),
  setManifest: (manifest) => set({ manifest }),
  markDataUpdated: (at) => set({ dataUpdatedAt: at }),
  setBasemap: (basemapId) => set({ basemapId }),
  setNotice: (notice) => set({ notice }),
  setPopup: (popup) => set({ popup }),
}));

export const selectActiveTheme = (s: Pick<MapState, 'config' | 'themeId'>) =>
  s.config?.themes.find((t) => t.id === s.themeId) ?? null;
